import {
  APP_LABELS,
  DEFAULT_LOCALE,
  appLabelOf,
  appParam,
  parseApp,
  stateTagColor,
  typeLabel,
  typeSlug,
} from './legalConstants';
import { Alert, Box, Button, Card, PageHeader, Tag, Text } from '@tocktix/pantry';
import { LegalPageFieldsFragment, usePendingLegalPageReviewsQuery } from 'generated/graphql';
import { hasEngRoleSelector } from 'selectors/rolesSelectors';
import { useActions } from 'utils/connect';
import { useNavigate, useParams } from 'react-router-v7';
import { useSelector } from 'react-redux';
import isLegalEditorSelector from 'selectors/isLegalEditorSelector';

/** Queue of legal pages for a single app awaiting review or publish. */
const LegalReviewsRoute: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { app: appParamRaw } = useParams<{ app: string }>();
  const app = parseApp(appParamRaw);
  const isLegalEditor = useSelector(isLegalEditorSelector);
  const isEng = useSelector(hasEngRoleSelector);
  const { actions } = useActions();
  const canReview = isEng || isLegalEditor;

  const { data, loading } = usePendingLegalPageReviewsQuery({
    skip: !canReview,
    fetchPolicy: 'cache-and-network',
    onError: () => actions.toast.showError("Couldn't load pending reviews."),
  });
  const pending: LegalPageFieldsFragment[] = (data?.pendingLegalPageReviews ?? [])
    .filter((page) => page.app === app)
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));

  if (!canReview) {
    return (
      <Box padding={[400, 500]}>
        <PageHeader title="Legal Page Reviews" />
        <Alert
          color="danger"
          message="You do not have access to this tool."
          data-testid="reviews-access-denied"
        />
      </Box>
    );
  }

  return (
    <Box padding={[400, 500]} display="flex" flexDirection="column" gap={300}>
      <PageHeader title={`${APP_LABELS[app]} Legal Page Reviews`} />
      <Box>
        <Button
          label="← Back"
          variant="text"
          color="secondary"
          size="compact"
          data-testid="reviews-back-btn"
          onClick={() => navigate('/legal')}
        />
      </Box>

      {!loading && pending.length === 0 && (
        <Text variant="body" color="fgSubtle" data-testid="reviews-empty">
          No pages are currently pending review or awaiting publish.
        </Text>
      )}

      {pending.map((page) => {
        const workflowId = page.reviewWorkflowId;
        return (
          <Card
            key={String(page.id)}
            style={workflowId ? { cursor: 'pointer' } : undefined}
            data-testid={`review-item-${page.id}`}
            onClick={
              workflowId
                ? () => navigate(`/legal/${appParam(app)}/reviews/${workflowId}`)
                : undefined
            }
          >
            <Card.Body>
              <Box display="flex" flexDirection="column" gap={50}>
                <Box display="flex" justifyContent="spaceBetween" alignItems="center" gap={100}>
                  <Box display="flex" alignItems="center" gap={100}>
                    <Text variant="bodyStrong">{typeLabel(page.type)}</Text>
                    <Tag label={appLabelOf(page)} color="primary" size="sm" />
                    {page.locale && <Tag label={page.locale} color="secondary" size="sm" />}
                  </Box>
                  <Tag label={page.state} color={stateTagColor(page.state)} size="sm" />
                </Box>
                {page.createdBy && (
                  <Text variant="caption" color="fgSubtle">
                    Created by {page.createdBy.fullName ?? page.createdBy.email}
                    {page.createdBy.fullName && ` (${page.createdBy.email})`}
                    {page.createdAt && ` on ${new Date(page.createdAt).toLocaleString()}`}
                  </Text>
                )}
                {page.state === 'APPROVED' && (
                  <Box>
                    <Button
                      label="Publish"
                      color="primary"
                      size="compact"
                      data-testid={`publish-btn-${page.id}`}
                      // Stop the card's review-detail navigation from also firing.
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(
                          `/legal/${appParam(app)}/${typeSlug(page.type)}?locale=${
                            page.locale ?? DEFAULT_LOCALE
                          }&draftId=${page.id}`
                        );
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Card.Body>
          </Card>
        );
      })}
    </Box>
  );
};

export default LegalReviewsRoute;
